import { FocusMonitor } from '@angular/cdk/a11y';
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { NgClass } from '@angular/common';
import { Component, ElementRef, HostBinding, Input, OnDestroy, OnInit, Optional, Self, ViewChild, computed, signal } from '@angular/core';
import { NgControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldControl } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTable, MatTableModule } from '@angular/material/table';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faGripLinesVertical } from '@fortawesome/free-solid-svg-icons';
import { Subject } from 'rxjs';
import { State } from 'src/app/components/designer/models/state';
import { StepStateFormDialogResult } from './Models/step-state-form-dialog-result';
import { StepStateFormDialogComponent } from './step-state-form-dialog/step-state-form-dialog.component';

/**
 * Composant de saisie des états d'une étape.
 * (Utilisable dans un formulaire et dans un MatFormField)
 */
@Component({
    selector: 'app-step-states-control',
    imports: [MatTableModule, MatIconModule, MatMenuModule, MatButtonModule, CdkDropList, FaIconComponent, CdkDragHandle, CdkDrag, NgClass],
    templateUrl: './step-states.control.html',
    styleUrl: './step-states.control.scss',
    providers: [
        {
            provide: MatFormFieldControl,
            useExisting: StepStateControl,
        },
    ]
})
export class StepStateControl implements OnInit, OnDestroy, MatFormFieldControl<State[]> {
    /**
     * Liste des icônes utilisés dans le template
     */
    public faGripLinesVertical = faGripLinesVertical;

    /**
     * Liste des colonnes affichées dans le tableau des états.
     */
    displayedColumns: string[] = ['order', 'name'];

    /**
     * Liste des enregistrements affichés dans le tableau.
     */
    stepStates = signal<State[]>([]);

    /**
     * Emet lorsque des changements surviennent dans le composant pour prevenir le MatFormField parent de déclencher le change detection.
     */
    stateChanges = new Subject<void>();

    /**
     * Associe les id des elements dont le contenu sera utilisé pour décrire ce composant à l'attribut aria.
     */
    @HostBinding('attr.aria-describedby') describedBy = '';

    /**
     * Définit les id des elements dont le contenu sera utilisé pour décrire ce composant.
     * @param ids Id des elements dont le contenu sera utilisé pour décrire ce composant.
     */
    setDescribedByIds(ids: string[]): void {
        this.describedBy = ids.join('');
    }

    /**
     * Définit la valeur du contrôle.
     */
    @Input()
    set value(value: State[]) {
        this.stepStates.set(value);
        this.stateChanges.next();
    }

    /**
     * Obtient la valeur du contrôle.
     */
    get value(): State[] {
        return this.stepStates();
    }

    /**
     * Valeur de l'identifiant qui sera attribuée à la prochaine instance du contrôle.
     */
    static nextId = 0;

    /**
     * Définit la valeur du placeholder.
     */
    set placeholder(placeholder: string) {
        // Le placeholder n'est pas utilisé dans ce composant
    }

    /**
     * Obtient la valeur du placeholder.
     */
    get placeholder() {
        return null;
    }

    /**
     * Retourne une valeur indiquant si le contrôle est vide (absence d'en-tête)
     */
    get empty(): boolean {
        return this.stepStates().length !== 0;
    }

    /**
     * Retourne une valeur indiquant si le libellé doit être flottant.
     * (non géré)
     */
    get shouldLabelFloat(): boolean {
        return false;
    }

    /**
     * Obtient une valeur indiquant si ce contrôle est obligatoire.
     */
    @Input()
    get required() {
        return this._required;
    }

    /**
     * Définit une valeur indiquant si ce contrôle est obligatoire.
     */
    set required(value: boolean) {
        this._required = value;
        this.stateChanges.next();
    }

    /**
     * Valeur indiquant si le contrôle est obligatoire.
     */
    private _required = false;

    /**
     * Obtient une valeur indiquant si ce contrôle est désactivé.
     */
    @Input()
    get disabled() {
        return this._disabled;
    }

    /**
     * Définit une valeur indiquant si ce contrôle est désactivé.
     */
    set disabled(value: boolean) {
        this._disabled = value;

        let actionColumnIndex = this.displayedColumns.findIndex((c) => c === 'actions');

        if (value === false) {
            if (actionColumnIndex === -1) {
                this.displayedColumns.push('actions');
            }
        } else {
            if (actionColumnIndex !== -1) {
                this.displayedColumns.splice(actionColumnIndex, 1);
            }
        }
    }

    /**
     * Valeur indiquant si ce contrôle est désactivé.
     */
    private _disabled = false;

    /**
     * Valeur indiquant si le contrôle possède le focus.
     */
    _focused: boolean = false;

    /**
     * Obtient une valeur indiquant si le contrôle possède le focus.
     */
    get focused() {
        return this._focused;
    }

    /**
     * Définit une valeur indiquant si le contrôle possède le focus.
     */
    set focused(value: boolean) {
        this._focused = value;
        this.onTouched(value);
    }

    /**
     * Fonction utilisée pour déterminer si la suppression d'un état est autorisée.
     */
    @Input()
    public isStateDeletionAllowed: (State) => boolean;

    /**
     * Id du contrôle.
     */
    @HostBinding()
    id = `StepStateInputDialogComponent-${StepStateControl.nextId++}`;

    /**
     * Réference à l'élément MatTable du template.
     */
    @ViewChild(MatTable, { read: ElementRef, static: true })
    table: ElementRef;

    /**
     * Constructeur.
     * @param focusMonitor : Service utilisé pour écouter les changements de focus.
     * @param dialog : Service utilisé pour ouvrir des boites de dialogue.
     * @param ngControl : Reference au contrôle dans le formulaire.
     */
    constructor(
        private focusMonitor: FocusMonitor,
        public dialog: MatDialog,
        @Optional() @Self() public ngControl: NgControl
    ) {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
        computed(() => console.log('Step states', this.stepStates));
    }

    /**
     * @description
     * Reports whether the control is dirty, meaning that the user has changed
     * the value in the UI. If the control is not present, null is returned.
     */
    // get dirty(): boolean | null {
    //     return false;
    // }

    /**
     * Renseigne la valeur du contrôle.
     * (Appellé par le formulaire)
     * @param value : valeur du contrôle.
     */
    writeValue(value: State[]): void {
        this.value = value;
    }

    /**
     * Fonction à appeler lorsque la valeur du contrôle change.
     */
    private onChange;

    /**
     * Inscrit une fonction à appeler lorsque la valeur du contrôle change.
     * (Appellé par le formulaire)
     * @param fn fonction à appeler lorsque la valeur du contrôle change.
     */
    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    /**
     * Fonction à appeler lorsque le contrôle gagne le focus.
     */
    private onTouched;

    /**
     * Inscrit une fonction à appeler lorsque le contrôle gagne le focus.
     * (Appellé par le formulaire)
     * @param fn fonction à appeler lorsque le contrôle gagne le focus.
     */
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    /**
     * Specifie l'état 'désactivé' du contrôle.
     * (Appellé par le formulaire)
     * @param value : Valeur de l'état 'désactivé'.
     */
    setDisabledState(value: boolean): void {
        this.disabled = value;
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le contrôle.
     * (Appellé par le formulaire)
     * @param event evnement clic de la souris.
     */
    onContainerClick(event: MouseEvent): void {
        // On ne fait rien de particulier lorsque l'on clique sur le tableau.
    }

    /**
     * Retourne une valeur indiquant si le contrôle est en erreur.
     */
    get errorState(): boolean {
        // On considère que ce control n'est jamais en erreur.
        return false;
    }

    /**
     * Appellé lors de l'initialisation du contrôle.
     */
    ngOnInit(): void {
        this.focusMonitor.monitor(this.table).subscribe((focused) => {
            this.focused = !!focused;
            this.stateChanges.next();
        });
    }

    /**
     * Appellé lors de la destruction du contrôle.
     */
    ngOnDestroy(): void {
        this.focusMonitor.stopMonitoring(this.table);
        this.stateChanges.complete();
    }

    /**
     * Appelé lorsque l'utilisateur double-clique sur un élément de la liste.
     */
    onMouseDoubleClick(row: State, event) {
        console.log('double clic', row);
        if (this.disabled) {
            this.displayState(row);
        } else {
            this.onEditState(row);
        }
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'élément 'Editer' du menu contextuel.
     * @param editedState : Instance d'état a éditer.
     */
    public onEditState(editedState: State): void {
        console.log('edit', editedState);

        const dialogRef = this.dialog.open(StepStateFormDialogComponent, {
            maxWidth: '400px',
            data: {
                title: "Édition de l'état",
                item: editedState,
                readonly: this.disabled,
            },
            panelClass: 'empty-dialog',
        });

        // Attente de la réponse de la boite de dialogue de saisie.
        dialogRef.afterClosed().subscribe((dialogResult: StepStateFormDialogResult) => {
            if (dialogResult && dialogResult.status === true) {
                // Mise à jour de l'état.
                console.log('update step state', dialogResult.item);
                this.stepStates.update((states) => {
                    let updatedStates = State.cloneArray(states);
                    const stateIndex = states.findIndex((x) => x.id === editedState.id);
                    updatedStates.splice(stateIndex, 1, dialogResult.item);

                    return updatedStates;
                });

                this.onChange(this.stepStates());
            }
        });
    }

    /**
     * Affiche un état en lecture seule.
     * @param state : Instance de l'état a afficher.
     */
    public displayState(state: State): void {
        console.log('display', state);

        const dialogRef = this.dialog.open(StepStateFormDialogComponent, {
            maxWidth: '400px',
            data: {
                title: "Consultation de l'état",
                item: state,
                readonly: this.disabled,
            },
            panelClass: 'empty-dialog',
        });
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton de création d'un état.
     */
    public onCreateState(): void {
        console.log('create');
        const state = new State();
        state.id = this.getNextId();

        const dialogRef = this.dialog.open(StepStateFormDialogComponent, {
            maxWidth: '400px',
            data: {
                title: "Ajout d'un état",
                item: state,
            },
            panelClass: 'empty-dialog',
        });

        // Attente de la réponse de la boite de dialogue de saisie.
        dialogRef.afterClosed().subscribe((dialogResult: StepStateFormDialogResult) => {
            if (dialogResult && dialogResult.status === true) {
                // Création de l'état.
                console.log('create state', dialogResult.item);
                dialogResult.item.id = this.getNextId();
                //                dialogResult.item.order = this.getLastPosition();
                this.stepStates.update((states) => {
                    let updatedStates = State.cloneArray(states);
                    updatedStates.push(dialogResult.item);

                    return updatedStates;
                });

                this.onChange(this.stepStates());
            }
        });
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'élément 'Dupliquer' du menu contextuel.
     * @param duplicatedState : Instance de l'état a dupliquer.
     */
    public onDuplicateState(duplicatedState: State): void {
        console.log('duplicate');
        duplicatedState = State.clone(duplicatedState);
        duplicatedState.id = null;

        const dialogRef = this.dialog.open(StepStateFormDialogComponent, {
            maxWidth: '400px',
            data: {
                title: "Ajout d'un état",
                item: State.clone(duplicatedState),
            },
            panelClass: 'empty-dialog',
        });

        // Attente de la réponse de la boite de dialogue de saisie.
        dialogRef.afterClosed().subscribe((dialogResult: StepStateFormDialogResult) => {
            if (dialogResult && dialogResult.status === true) {
                // Création de l'état.
                console.log('create state', dialogResult.item);
                dialogResult.item.id = this.getNextId();
                //dialogResult.item.order = this.getLastPosition();
                this.stepStates.update((states) => {
                    let updatedStates = State.cloneArray(states);
                    updatedStates.push(dialogResult.item);

                    return updatedStates;
                });

                this.onChange(this.stepStates());
            }
        });
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'élément 'Supprimer' du menu contextuel.
     * @param event : Instance de l'état a supprimer.
     */
    public onDeleteState(event: State): void {
        console.log('delete', event);
        if (this.isStateDeletionAllowed(event)) {
            this.stepStates.update((steps) => steps.filter((step) => step.id !== event.id));
            this.onChange(this.stepStates());
        }
    }

    /**
     * Appellé lorsque l'utilisateur modifie l'ordre d'un état.
     * @param event : Instance de l'évènement de drag&drop.
     */
    public onStateDrop(event: CdkDragDrop<string[]>) {
        let states = this.stepStates();
        moveItemInArray(states, event.previousIndex, event.currentIndex);
        let index = 1;
        // states.forEach((x) => {
        //     x.order = index++;
        // });

        this.stepStates.set(State.cloneArray(states));
        this.onChange(this.stepStates());
    }

    /**
     * Retourne l'id autogénéré pour l'état.
     * @returns un Id.
     */
    private getNextId() {
        let nextId = 1;

        if (this.stepStates().length !== 0) {
            nextId = this.stepStates().reduce((a1, a2) => (a1.id > a2.id ? a1 : a2)).id + 1;
        }

        return nextId;
    }

    // /**
    //  * Retourne la position du nouvel état.
    //  * @returns une position à la fin de la liste.
    //  */
    // private getLastPosition() {
    //     let lastPosition = 1;

    //     if (this.stepStates().length !== 0) {
    //         lastPosition = this.stepStates().reduce((a1, a2) => (a1.id > a2.id ? a1 : a2)).order + 1;
    //     }

    //     return lastPosition;
    // }
}
